/**
 * Digital Voting — shared display helpers + small UI primitives.
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/index.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting)
 */

import React from 'react';
import type { VotingItemStatus, VotingSessionStatus } from './votingApi';

export const SESSION_STATUS_META: Record<VotingSessionStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: '#4b5563', bg: '#e5e7eb' },
  scheduled: { label: 'Scheduled', color: '#1d4ed8', bg: '#dbeafe' },
  live: { label: 'Live', color: '#047857', bg: '#d1fae5' },
  paused: { label: 'Paused', color: '#b45309', bg: '#fef3c7' },
  closed: { label: 'Closed', color: '#166534', bg: '#dcfce7' },
  cancelled: { label: 'Cancelled', color: '#b91c1c', bg: '#fee2e2' },
};

export const ITEM_STATUS_META: Record<VotingItemStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: '#6b7280' },
  showing: { label: 'Showing', color: '#2563eb' },
  open: { label: 'Open', color: '#059669' },
  closed: { label: 'Closed', color: '#374151' },
};

export const OUTCOME_META: Record<string, { label: string; color: string; bg: string }> = {
  passed: { label: 'Passed', color: '#166534', bg: '#dcfce7' },
  failed: { label: 'Failed', color: '#b91c1c', bg: '#fee2e2' },
  tied: { label: 'Tied', color: '#b45309', bg: '#fef3c7' },
  no_quorum: { label: 'No Quorum', color: '#9d174d', bg: '#fce7f3' },
};

export const SESSION_TYPE_LABELS: Record<string, string> = {
  agm: 'AGM',
  sgm: 'SGM',
  election: 'Election',
  poll: 'Poll',
  general: 'General',
};

export const ITEM_TYPE_LABELS: Record<string, string> = {
  resolution: 'Resolution',
  motion: 'Motion',
  election: 'Election',
  poll: 'Poll',
  budget: 'Budget',
};

export function Badge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
      style={{ color, backgroundColor: bg }}
    >
      {label}
    </span>
  );
}

export function StatusBadge({ status }: { status: VotingSessionStatus }) {
  const meta = SESSION_STATUS_META[status] ?? SESSION_STATUS_META.draft;
  return <Badge label={meta.label} color={meta.color} bg={meta.bg} />;
}

export function OutcomeBadge({ outcome }: { outcome: string | null }) {
  if (!outcome) return null;
  const meta = OUTCOME_META[outcome];
  if (!meta) return <Badge label={outcome} color="#374151" bg="#e5e7eb" />;
  return <Badge label={meta.label} color={meta.color} bg={meta.bg} />;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-ZA', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatWindow(session: {
  delivery_mode: string;
  scheduled_start?: string | null;
  voting_opens_at?: string | null;
  voting_closes_at?: string | null;
}): string {
  if (session.delivery_mode === 'async') {
    const open = formatDateTime(session.voting_opens_at);
    const close = formatDateTime(session.voting_closes_at);
    return `${open} → ${close}`;
  }
  const start = formatDateTime(session.scheduled_start);
  // Meeting + pre-vote: show the meeting time with the ballot window alongside.
  if (session.voting_opens_at) {
    return `${start} · pre-vote ${formatDateTime(session.voting_opens_at)} → ${formatDateTime(session.voting_closes_at)}`;
  }
  return start;
}
